"use client";
import { cn } from "@/lib/utils";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { cva, VariantProps } from "class-variance-authority";
import { NumericFormat } from "react-number-format";

import { Field, getIn, useFormikContext } from "formik";
import { ChevronDownIcon } from "lucide-react";
import React, {
  forwardRef,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  useMemo,
  useState,
} from "react";

const inputVariants = cva(
  [
    "transition-all border rounded-md outline-none",
    "bg-white dark:bg-black dark:text-white",
    "focus:border-primary focus:ring-1 focus:ring-primary focus:ring-inset",
    "disabled:border-secondary/60 disabled:text-foreground/40 disabled:bg-secondary/20",
  ],
  {
    variants: {
      inputSize: {
        sm: "px-2 py-1 text-sm h-8",
        md: "px-3 py-1 h-9",
        lg: "px-3 py-2 h-10",
      },
    },
    defaultVariants: {
      inputSize: "lg",
    },
  }
);

const textareaVariants = cva([
  "bg-white dark:bg-black",
  "transition-all border rounded-md outline-none",
  "dark:bg-black dark:text-white",
  "focus:border-primary focus:ring-1 focus:ring-primary focus:ring-inset",
  "disabled:border-secondary/60 disabled:text-foreground/40 disabled:bg-secondary/20",
  "p-3",
]);

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  mask?: string;
}

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement>,
    VariantProps<typeof inputVariants> {
  inputClassName?: string;
}

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const InputLabel: React.FC<any> = ({
  children = null,
  required = false,
  className = "",
}) => {
  return (
    <label
      className={cn(
        "block mb-1 font-semibold text-gray-700 truncate font-worksans dark:text-gray-300",
        className
      )}
    >
      {children}
      {required && <span className="text-red-500">*</span>}
    </label>
  );
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { inputSize, className, ...props },
  ref
) {
  if (props.type === "checkbox") {
    return <input {...props} ref={ref} className={cn("", className)} />;
  }

  return (
    <input
      {...props}
      ref={ref}
      className={cn(inputVariants({ inputSize }), className)}
    />
  );
});

export const FieldInput = (props: InputProps) => {
  return <Field {...props} as={Input} />;
};

export const FieldSelect = ({
  className,
  inputClassName,
  inputSize,
  ...props
}: SelectProps) => {
  return (
    <div className={cn("relative", className)}>
      <div className="top-0 right-3 bottom-0 absolute flex items-center pointer-events-none">
        <ChevronDownIcon className="size-4 text-foreground" />
      </div>
      <Field
        as="select"
        {...props}
        className={cn(
          inputVariants({ inputSize }),
          "appearance-none w-full !pr-9",
          inputClassName
        )}
      />
    </div>
  );
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        {...props}
        ref={ref}
        className={cn(textareaVariants(), className)}
      />
    );
  }
);

export const FieldTextarea = ({ ...props }: TextareaProps) => {
  return <Field {...props} as={Textarea} />;
};

export const InputTags = ({ name }) => {
  const [value, setValue] = useState("");
  const { values, setFieldValue } = useFormikContext();
  const tags = useMemo(() => {
    const tags = getIn(values, name) || [];
    return tags;
  }, [name, values]);

  const appendTags = (value) => {
    if (tags.indexOf(value) === -1) {
      const values = [...tags];
      values.push(value);
      setFieldValue(name, values);
    }
  };

  const deleteTags = (index) => {
    const values = [...tags];
    values.splice(index, 1);
    setFieldValue(name, [...values]);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center bg-white dark:bg-black border rounded-md overflow-hidden">
        {tags.map((item, index) => (
          <div
            className="flex items-center bg-slate-200 dark:bg-gray-800 m-1 px-1 rounded-md"
            key={index}
          >
            <span>{item}</span>
            <button
              type="button"
              className="bg-red-500 ml-1 rounded-full text-white"
              onClick={() => deleteTags(index)}
            >
              <XMarkIcon className="w-3" />
            </button>
          </div>
        ))}{" "}
        <input
          type="text"
          placeholder="New Tags"
          value={value}
          className="block bg-transparent px-2 py-1 outline-none appearance-none"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.code === "Backspace" && value === "") {
              e.preventDefault();
              deleteTags(tags.length - 1);
            }
            if (e.code === "Enter") {
              e.preventDefault();
              if (value !== "") {
                setValue("");
                appendTags(value);
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export const NumberFormat = ({
  className,
  mask,
  inputSize,
  ...props
}: InputProps) => {
  return (
    <NumericFormat
      // {...props}
      value={props.value as any}
      onValueChange={(value) => {
        if (props.onChange) {
          props.onChange({
            target: {
              name: props.name,
              value: value.value,
            },
          } as any);
        }
      }}
      thousandsGroupStyle="thousand"
      thousandSeparator="."
      decimalSeparator=","
      disabled={props.disabled}
      className={cn(inputVariants({ inputSize }), className)}
    />
  );
};

export const FieldNumberFormat = ({ mask, ...props }: InputProps) => {
  return (
    <Field name={props.name}>
      {({ field, form }) => (
        <NumberFormat
          {...props}
          {...field}
          onChange={(e) => form.setFieldValue(field.name, e.target.value)}
        />
      )}
    </Field>
  );
};
